import { Component, Host, h, Prop, Element, Event, type EventEmitter } from '@stencil/core'
import { type CobaltIcon } from '../../../cobalt-icon/cobalt-icon'

export interface CheckboxItemType {
  value: string
  label: string
  description?: string
  iconName?: never
  disabled?: boolean
  selected?: boolean
}

export interface MultipleSelectionSectionType {
  sectionTitle?: string
  sectionId: string
  format?: 'multi-selection'
  selectedItems?: string[]
  items: CheckboxItemType[]
}

export interface SelectionItemType {
  value: string
  label: string
  description?: string
  iconName?: CobaltIcon['name']
  disabled?: boolean
  selected?: boolean
}

export interface SelectionSectionType {
  sectionTitle?: string
  sectionId?: string
  format?: 'selection'
  selectedItems?: string[]
  items: SelectionItemType[]
}

export interface ActionSectionType {
  sectionTitle?: string
  description?: string
  format: 'action'
  selectedItems?: never
}

let id = 0

@Component({
  tag: 'cobalt-dropdown-section'
})
export class CobaltDropdownSection {
  sectionIdIncremented = `dropdown-section-${++id}`
  /**
   * The format of the section will define the type of items to display.
   */
  @Prop({ mutable: true }) format!: SelectionSectionType['format'] | MultipleSelectionSectionType['format'] | ActionSectionType['format']
  /**
   * Title of the section.
   */
  @Prop({ mutable: true }) sectionTitle!: string
  /**
   * Description of the section.
   */
  @Prop({ mutable: true }) description?: string
  /**
   * Items are defined in the `items` property in the `selection` and `multi-selection` format.
   * It is used only for selection purpose.
   *
   * For other purposes use the `action` format and the `<cobalt-dropdown-action-item>` component in slot.
   */
  @Prop({ mutable: true }) items!: string | SelectionSectionType['items'] | MultipleSelectionSectionType['items']
  /**
   * Adding a divider on top of the section.
   */
  @Prop() divider?: boolean

  /**
   * Emitted when the value has changed.
   */
  @Event() cobaltDropdownSectionsItemsChange: EventEmitter
  @Element() element: HTMLElement

  componentDidLoad(): void {
    this.moveActionitemIntoListItem()
  }

  moveActionitemIntoListItem(): void {
    const fragment = document.createDocumentFragment()
    this.element.querySelectorAll('cobalt-dropdown-action-item').forEach(item => {
      const li = fragment.appendChild(document.createElement('li'))
      li.classList.add('list-item-action')
      li.classList.add('item')
      item.disabled && li.classList.add('item--disabled')
      li.classList.add('typography-body-medium-regular')
      li.setAttribute('tabindex', '0')
      li.append(item)
    })
    this.element.querySelector('ul').appendChild(fragment)
  }

  render(): any {
    return (
      <Host>
        <core-dropdown-section
          format={this.format}
          sectionTitle={this.sectionTitle}
          description={this.description}
          divider={this.divider}
          items={this.items}
          onCoreDropdownSectionsItemsChange={event => {
            this.cobaltDropdownSectionsItemsChange.emit(event.detail)
          }}
        ></core-dropdown-section>
      </Host>
    )
  }
}
